import { useCallback, useState } from 'react';
import { Project } from '../../../types';
import { uploadScript } from '../../../api';

interface UseForcedConfigOptions {
  projectId?: string;
  /** 强制上传剧本成功后的刷新回调（拿到新 script 后让 needsScript 翻转、弹窗自动放行）。 */
  onUploaded?: () => void;
  /** 上传失败时的错误提示回调。 */
  onError?: (message: string) => void;
  /** 上传失败的兜底文案。 */
  uploadFailedMessage: string;
}

/**
 * 硬强制完成度判定 + 强制剧本上传，供「资产设定页」与「剧本信息页」共用（§3-②）。
 *
 * - 完成度 = 风格已配(baseConfigCompleted) ∧ 剧本已传(script 存在且非 failed)。
 * - needsStyle / needsScript 互斥（needsScript 要求 baseConfigCompleted 为真）。
 * - 缺哪步，落地页就把对应弹窗设为 dismissible=false（不可空关）+ solidBackdrop（白底），
 *   缺剧本时选文件即直接 uploadScript，成功刷新后自动放行。
 */
export function useForcedConfig(project: Project | null, opts: UseForcedConfigOptions) {
  const { projectId, onUploaded, onError, uploadFailedMessage } = opts;
  const [forcedUploading, setForcedUploading] = useState(false);

  const scriptOk = !!project?.script && project.script.status !== 'failed';
  const needsStyle = !!project && !project.baseConfigCompleted;
  const needsScript = !!project && !!project.baseConfigCompleted && !scriptOk;

  const handleForcedScriptUpload = useCallback(async (file: File) => {
    if (!projectId) return;
    setForcedUploading(true);
    try {
      await uploadScript(projectId, file);
      onUploaded?.();
    } catch (err: unknown) {
      onError?.(err instanceof Error ? err.message : uploadFailedMessage);
    } finally {
      setForcedUploading(false);
    }
  }, [projectId, onUploaded, onError, uploadFailedMessage]);

  return { needsStyle, needsScript, forcedUploading, handleForcedScriptUpload };
}
